<template>
  <div class="device_select_stage" ref="deviceSelectStage">
    <virtual_devices
      v-slot="{ kk }"
      :is-continuous-select="continuousSelect"
      :key-class-resolver="resolveDeviceKeyClass"
      ref="virtualDevices"
      @key-click="selectKeyClick"
      @key-mouse-enter="handleMouseEnter"
      @key-mouse-leave="handleMouseLeave"
      @continuous-select-key="continuousSelectKey"
    >
      <template v-if="operate.getActionType().value == OperateType.light">
        <Set_light_device :kk="kk" />
      </template>

      <template v-else-if="operate.getActionType().value == OperateType.action">
        <Set_action_device :kk="kk" />
      </template>

      <template v-else-if="operate.getActionType().value == OperateType.mgx">
        <Set_magnet_device :kk="kk" />
      </template>
    </virtual_devices>

    <Mgx_hover_card :hover-element="hoverHTMLElement" :hover-key="hoverMagnetKK" :stage-element="deviceSelectStage" />

    <Select v-if="hoverHTMLElement" :currentSelectElement="hoverHTMLElement"></Select>
  </div>
</template>

<script setup lang="ts">
import { operate, OperateType } from '@/service/driver/operate'
import virtual_devices from './virtual_devices.vue'
import { setData } from '@/service/driver/data'
import { KK, VK } from '@/define/define'
import { computed, ref, watch } from 'vue'
import { lightSet } from '@/service/driver/light_set'
import Set_magnet_device from './set_magnet_device.vue'
import Set_action_device from './set_action_device.vue'
import Set_light_device from './set_light_device.vue'
import Mgx_hover_card from './mgx_hover_card.vue'
import { magnetSet } from '@/service/driver/magnet_set'
import Select, { hoverKK } from './select.vue'
import { magnetSetKeyGroup } from '@/service/driver/magnet_key_group'
import { IMCSwitchState, isFE87V2IMCNH } from '../mgx_set/IMC_switch.vue'
import { getCurrentDevice } from '@/profiles/selector'
import { DeviceType } from '@libs/types/enum'

const hoverHTMLElement = ref<HTMLElement | null>(null)
const deviceSelectStage = ref<HTMLElement | null>(null)
const virtualDevices = ref<InstanceType<typeof virtual_devices> | null>(null)
const hoverMagnetKK = ref<KK | null>(null)

const selectKeyClick = (kk: KK, _vk: VK) => {
  if (operate.getActionType().value == OperateType.mgx) {
    if ((getCurrentDevice.value.configurableMagneticAxis.length > 0 && !getCurrentDevice.value.configurableMagneticAxis.includes(kk)) || IMCSwitchState.value == true || isFE87V2IMCNH() || !magnetSet.list[kk]) {
      return
    }

    if (magnetSet.selectedKeySet.value.has(kk)) {
      magnetSet.selectedKeys.value = [...magnetSet.selectedKeys.value.filter((item) => item != kk)]
    } else {
      magnetSet.selectedKeys.value = [...magnetSet.selectedKeys.value, kk]
    }
  }

  if (operate.getActionType().value == OperateType.light && setData.getData().LightLastIsCustom) {
    lightSet.lightData = {
      ...lightSet.lightData,
      LightLastIsCustom: true,
      ColorsCustom: { ...lightSet.lightData.ColorsCustom, [kk]: lightSet.foregroundColor.value },
    }
    // setData.setCustomLight(kk, lightSet.foregroundColor.value)
    // setData.setKeysColor(setData.getData().LightCustom, setData.getData().LightCustom)
  }
  if (operate.getActionType().value == OperateType.action) {
    operate.currentKey.value = kk
  }
}
const continuousSelectKey = (keys: KK[]) => {
  if (operate.getActionType().value == OperateType.mgx) {
    if (IMCSwitchState.value == true || isFE87V2IMCNH()) {
      return
    }
    if (getCurrentDevice.value.configurableMagneticAxis.length > 0) {
      keys = keys.filter((skk: KK) => {
        return getCurrentDevice.value.configurableMagneticAxis.includes(skk)
      })
    }
    const addFilterKKs: KK[] = []
    keys.forEach((skk: KK) => {
      if (magnetSet.list[skk]) {
        addFilterKKs.push(skk)
      }
    })
    magnetSet.selectedKeys.value = [...addFilterKKs]
  } else if (operate.getActionType().value == OperateType.light && setData.getData().LightLastIsCustom) {
    const kks: { [index: KK]: number } = { ...setData.getData().LightCustom }
    keys.forEach((kk: KK) => {
      kks[kk] = lightSet.foregroundColor.value
    })
    lightSet.lightData = {
      ...lightSet.lightData,
      LightLastIsCustom: true,
      ColorsCustom: kks,
    }
    //  keys.forEach((kk) => {
    //   setData.setCustomLight(kk, lightSet.foregroundColor.value)
    // })
    // setData.setKeysColor(setData.getData().LightCustom, setData.getData().LightCustom)
  }
}
// 移入
const handleMouseEnter = (event: MouseEvent, kk: KK, _vk: VK) => {
  hoverHTMLElement.value = event.currentTarget as HTMLElement
  hoverMagnetKK.value = kk
  // console.log('hoverHTMLElement', kk)
  hoverKK.value = kk
}
// 移出
const handleMouseLeave = (_event: MouseEvent, _kk: KK, _vk: VK) => {
  hoverHTMLElement.value = null
  hoverMagnetKK.value = null
  hoverKK.value = null
}

const continuousSelect = computed(() => {
  return operate.getActionType().value == OperateType.mgx || (operate.getActionType().value == OperateType.light && setData.getData().LightLastIsCustom)
})

const hasMagnetPreviewData = (kk: KK): boolean => {
  return magnetSet.list[kk] != null && IMCSwitchState.value === false && isFE87V2IMCNH() === false
}

const hasActionRemapData = (kk: KK): boolean => {
  const data = setData.getActionData
  if (data == undefined) {
    return false
  }

  return Boolean(data.KeyMap[kk] || data.Macros[kk] || data.Magenet[kk] || data.KeyMapModifier[kk] || (data.KeyMapFire && data.KeyMapFire[kk]))
}

const resolveDeviceKeyClass = (kk: KK): string[] => {
  const classes: string[] = []

  if (operate.getActionType().value === OperateType.mgx) {
    if (hasMagnetPreviewData(kk)) {
      classes.push('mgx_key_has_data')
    }

    if (magnetSet.selectedKeySet.value.has(kk)) {
      classes.push('mgx_key_selected')
    }
  }

  if (operate.getActionType().value === OperateType.action && getCurrentDevice.value.deviceType === DeviceType.keyboard) {
    const isCurrentKey = operate.currentKey.value === kk
    const isRemapped = hasActionRemapData(kk)

    if (isRemapped) {
      classes.push(operate.fnCheck.value ? 'action_key_fn_remapped' : 'action_key_remapped')
    }

    if (isCurrentKey) {
      classes.push('action_key_current')

      if (isRemapped) {
        classes.push('action_key_current_remapped')
      }
    }
  }

  return classes
}

// 清除连续选择的按键
watch(operate.getActionType(), (newVal, _oldVal) => {
  if (newVal == OperateType.mgx) {
    virtualDevices.value?.clearContiguousSelection()
  }
  if (newVal == OperateType.light && setData.getData().LightLastIsCustom) {
    virtualDevices.value?.clearContiguousSelection()
  }
})
watch(
  () => Object.keys(setData.getData().LightCustom).length,
  (newVal, oldVal) => {
    if (newVal == 0 && oldVal > 0) {
      virtualDevices.value?.clearContiguousSelection()
    }
  },
)
watch(lightSet.foregroundColor, (newVal, oldVal) => {
  if (newVal != oldVal) {
    virtualDevices.value?.clearContiguousSelection()
  }
})
watch(
  () => magnetSetKeyGroup.state.keysGroupType,
  (newVal, oldVal) => {
    if (newVal != oldVal) {
      virtualDevices.value?.clearContiguousSelection()
    }
  },
)

watch(operate.fnCheck, (newVal, oldVal) => {
  if (newVal != oldVal) {
    hoverHTMLElement.value = null
    hoverMagnetKK.value = null
    hoverKK.value = null
  }
})
</script>

<style lang="less">
.device_select_stage {
  position: relative;
  width: fit-content;
  overflow: visible;
}

.custom_color {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  border-radius: 4px;
}
</style>
